import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spin } from 'antd';

import RightOperate from './components/RightOperate';
import Drawer, { ControlBtnNode } from '@/components/Drawer';
import Legend from './components/Legend';
import SearchTools from './components/SearchTools';
import { MapContainer } from './components/MapContainer';
import { MapEditorPopupPortal } from './components/MapEditorPopupPortal';
import './index.scss';

import {
  useEditorStore,
  useSearchStore,
  useLayerStore,
  useDataStore,
  useRoutePlanStore,
  selectIsRoutePicking,
} from './store';
import { useMapBootstrap } from './hooks/useMapBootstrap';
import { useSearchTools } from './hooks/useSearchTools';
import { useRightOperate } from './hooks/useRightOperate';
import { useRoutePlan } from './hooks/useRoutePlan';
import RoutePlanFloatPanel from './components/RoutePlanFloatPanel';
import { LightMapAssistType } from '@/utils/constant';
import {
  emptyNavigationEnumOptions,
  getNavigationEnumOptions,
  hasNavigationEnumOptions,
  type NavigationEnumOptions,
} from './utils/navigation';
import { mapEditorFetch } from './utils/fetch';
import { HttpStatusCode } from '@/fetch/core/constant';

/** searchCallBack 未就绪时的稳定空实现，避免每次渲染新建函数打断 SearchTools 的 React.memo */
const NOOP_SEARCH_CALLBACK = () => undefined;

/**
 * 地图编辑器页面入口
 *
 * 职责：
 * 1. 页面整体布局：地图容器 + 搜索工具 + 右侧抽屉 + 弹窗 portal
 * 2. 订阅必要的 UI 状态（session、图层显隐等）
 * 3. 通过 useMapBootstrap 获取地图/Controller 实例，通过 useSearchTools / useRightOperate
 *    把业务回调分发给 SearchTools / RightOperate
 * 4. 渲染 MapEditorPopupPortal，由该组件自行订阅 popupContent 并处理弹窗交互

 * 不包含：地图 SDK 加载、业务搜索逻辑、CRUD 逻辑、绘制状态机 —— 这些已下沉到
 * controller / managers / hooks 中。
 */
const MapEditor: React.FC = () => {
  // ---- Refs ----
  const mapSdkMountRef = useRef<HTMLDivElement | null>(null);
  const mapViewportRef = useRef<HTMLDivElement | null>(null);
  const overlayRootRef = useRef<HTMLDivElement | null>(null);
  const popupRootRef = useRef<HTMLDivElement | null>(null);

  // ---- Local state ----
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);

  // ---- Zustand stores ----
  const session = useEditorStore((s) => s.session);

  const variableList = useDataStore((s) => s.variableList);
  const pageData = useDataStore((s) => s.pageData);

  const positionSearchVal = useSearchStore((s) => s.positionSearchVal);
  const setPositionSearchVal = useSearchStore((s) => s.setPositionSearchVal);
  const rightSearchVal = useSearchStore((s) => s.rightSearchVal);
  const stopOptionList = useSearchStore((s) => s.stopOptionList);
  const vehicleOptionList = useSearchStore((s) => s.vehicleOptionList);
  const cityList = useSearchStore((s) => s.cityList);

  const hiddenDynamicLayerIds = useLayerStore((s) => s.hiddenDynamicLayerIds);
  const hiddenWmsLayerIds = useLayerStore((s) => s.hiddenWmsLayerIds);
  const ldMapVisible = useLayerStore((s) => s.ldMapVisible);
  const toggleLdMap = useLayerStore((s) => s.toggleLdMap);

  // ---- 路径规划状态 ----
  const planningMode = useRoutePlanStore((s) => s.planningMode);
  // 选点阶段（共享谓词）：地图光标变为选点态
  const isRoutePicking = useRoutePlanStore(selectIsRoutePicking);

  // ---- 导航枚举状态 ----
  const [navigationEnums, setNavigationEnums] =
    useState<NavigationEnumOptions>(emptyNavigationEnumOptions);
  const [navigationEnumStatus, setNavigationEnumStatus] = useState<
    'loading' | 'ready' | 'error'
  >('loading');
  const navigationEnumRequestRef = useRef(0);

  const loadNavigationEnums = useCallback(async () => {
    const requestId = ++navigationEnumRequestRef.current;
    setNavigationEnumStatus('loading');
    try {
      const response = await mapEditorFetch.getEnum();
      const options = getNavigationEnumOptions(response?.data);
      if (
        response?.code !== HttpStatusCode.Success ||
        !hasNavigationEnumOptions(options)
      ) {
        throw new Error(response?.message || '导航枚举加载失败');
      }
      if (requestId !== navigationEnumRequestRef.current) return;
      setNavigationEnums(options);
      setNavigationEnumStatus('ready');
    } catch (error) {
      if (requestId !== navigationEnumRequestRef.current) return;
      setNavigationEnums(emptyNavigationEnumOptions);
      setNavigationEnumStatus('error');
    }
  }, []);

  useEffect(() => {
    loadNavigationEnums();
  }, [loadNavigationEnums]);

  // ---- Derived state ----
  const isDrawing = session.mode === 'createDrawing';
  const showEditForm = session.mode === 'edit' || session.mode === 'createForm';
  // 轻图测试两步绘制期间保留 lightMapDraft，此时右侧面板需要可点击（重绘/继续/提交），
  // 因此只有在普通单步绘制且没有 lightMapDraft 时才显示右侧蒙层。
  const showRightMask = session.mode === 'createDrawing' && !session.lightMapDraft;
  const curSelectPolygonData = session.activePolygonData;
  const rightPanelMode: 'list' | 'form' = showEditForm ? 'form' : 'list';
  const formMode: 'add' | 'edit' = session.mode === 'edit' ? 'edit' : 'add';
  const hiddenLayerIds = useMemo(
    () => [...hiddenDynamicLayerIds, ...hiddenWmsLayerIds],
    [hiddenDynamicLayerIds, hiddenWmsLayerIds]
  );

  // ---- Controller & hooks ----
  const {
    isBaiduMapReady,
    baiduMapError,
    isControllerReady,
    isMapTilesLoaded,
    coordinatorRef,
    locationOptionList,
    setLocationOptionList,
    isLocationDropdownOpen,
    setIsLocationDropdownOpen,
  } = useMapBootstrap(mapSdkMountRef, overlayRootRef);

  // 地图完全就绪（Coordinator 创建完成 + 瓦片首次加载完成）：未就绪前工具栏/右侧面板显示
  // 局部 loading 并禁用交互，避免 micro-app 宿主中 BMapGL 初始化窗口期操作下拉弹层（见 AGENTS.md 坑17）
  const isMapReady = isControllerReady && isMapTilesLoaded;

  const searchTools = useSearchTools(
    coordinatorRef,
    setPositionSearchVal,
    setLocationOptionList,
    setIsLocationDropdownOpen
  );

  /**
   * antd 弹层（Popconfirm/Tooltip/Modal）的挂载容器。
   * 注意：挂到 popupRoot（z-index:1100，高于抽屉），而不是详情面板所在的
   * overlayRoot（z-index:950，低于抽屉），否则抽屉内“停用/删除”的气泡
   * 会被抽屉压住。
   */
  const getOverlayContainer = useCallback(
    () =>
      popupRootRef.current ??
      overlayRootRef.current ??
      mapViewportRef.current?.parentElement ??
      document.body,
    []
  );

  const rightOperate = useRightOperate(coordinatorRef, getOverlayContainer);

  // 路径规划浮层 props（订阅 routePlanStore + 回调注入）
  const routePlanPanelProps = useRoutePlan(coordinatorRef);

  // ---- 轻图类型选择回调 ----
  const handleLightMapTypeSelect = useCallback(() => {
    // 用户在 EditForm 中选择了“轻图测试”类型
    // 设置 pendingCreateType，等待用户选择子类型
    // 不清除当前绘制的多边形，子类型选择时会复用它
  }, []);

  // ---- 轻图子类型选择回调 ----
  const handleLightMapSubTypeChange = useCallback(
    (subType: LightMapAssistType) => {
      const ctrl = coordinatorRef.current;
      if (!ctrl) return;
      if (subType === LightMapAssistType.BUFFER_ZONE) {
        // 缓冲区：复用已绘制的多边形作为缓冲区，进入 bufferReady
        ctrl.startLightMapBufferFromDraw();
      } else if (subType === LightMapAssistType.WARNING_ZONE) {
        // 危险区：应用危险区样式，保持 EditForm 可见让用户填写备注
        // 不设置 lightMapDraft，用户通过 createPolygon 提交
        ctrl.applyLightMapWarningStyle();
      }
    },
    [coordinatorRef]
  );

  // ---- Effects ----

  /** 编辑表单出现时自动打开抽屉 */
  useEffect(() => {
    if (showEditForm) {
      setIsDrawerOpen(true);
    }
  }, [showEditForm]);

  /** 抽屉状态变化时通知地图重新计算尺寸 */
  const handleMapResize = useCallback(() => {
    coordinatorRef.current?.map?.changeMapSize?.();
  }, [coordinatorRef]);

  // ---- Drawer 控制按钮 ----

  /** 打开右侧抽屉 */
  const handleOpenDrawer = useCallback(() => setIsDrawerOpen(true), []);
  /** 关闭右侧抽屉 */
  const handleCloseDrawer = useCallback(() => setIsDrawerOpen(false), []);

  /** 抽屉打开按钮（折叠态显示） */
  const openBtnNode = useMemo(
    () => (
      <ControlBtnNode
        direction="left"
        type="editForm"
        clickCallBack={handleOpenDrawer}
      />
    ),
    [handleOpenDrawer]
  );

  /** 抽屉关闭按钮（展开态显示） */
  const closeBtnNode = useMemo(
    () => (
      <ControlBtnNode
        direction="right"
        type="editForm"
        clickCallBack={handleCloseDrawer}
      />
    ),
    [handleCloseDrawer]
  );

  // ---- 渲染 ----

  if (baiduMapError) {
    return <div>{baiduMapError.message}</div>;
  }

  if (!isBaiduMapReady) {
    return <div>百度地图 SDK 加载中...</div>;
  }

  return (
    <div className="map-editor-container">
      <div ref={overlayRootRef} className="map-editor-overlay-root" />
      {/* antd 弹层挂载层（Popconfirm/Tooltip/Modal）：层级高于右侧抽屉，见 zIndex.ts */}
      <div ref={popupRootRef} className="map-editor-popup-root" />

      {/* 地图多边形选中弹窗：通过 portal 渲染到百度地图覆盖物创建的 DOM 节点中 */}
      <MapEditorPopupPortal
        coordinatorRef={coordinatorRef}
        getOverlayContainer={getOverlayContainer}
        navigationEnums={navigationEnums}
      />

      <MapContainer
        mapSdkMountRef={mapSdkMountRef}
        mapViewportRef={mapViewportRef}
        sessionMode={session.mode}
        isRoutePicking={isRoutePicking}
        showEditForm={showEditForm}
        isDrawerOpen={isDrawerOpen}
        onResize={handleMapResize}
      >
        <SearchTools
          searchForm={positionSearchVal}
          isLocationDropdownOpen={isLocationDropdownOpen}
          isMapReady={isMapReady}
          showEditForm={showEditForm}
          isDrawing={isDrawing}
          planningMode={planningMode}
          locationOptionList={locationOptionList}
          stopOptionList={stopOptionList}
          vehicleOptionList={vehicleOptionList}
          cityList={cityList}
          unSelectedLayer={hiddenLayerIds}
          ldMapVisible={ldMapVisible}
          toggleLdMap={toggleLdMap}
          searchCallBack={coordinatorRef.current?.searchCallBack ?? NOOP_SEARCH_CALLBACK}
          confirmSearch={searchTools.confirmSearch}
          handleLocationSearch={searchTools.handleLocationSearch}
          changeCascader={searchTools.changeCascader}
          handleSelectStop={searchTools.handleSelectStop}
          handleSelectlatlng={searchTools.handleSelectlatlng}
          changeLayerVisible={searchTools.changeLayerVisible}
          onClickDraw={searchTools.startPaint}
          cancelPaint={searchTools.cancelPaint}
          onToggleRoutePlan={searchTools.toggleRoutePlan}
          handleBlur={searchTools.handleBlur}
          handleFocus={searchTools.handleFocus}
        />
        {planningMode && <RoutePlanFloatPanel {...routePlanPanelProps} />}
        <Legend />
      </MapContainer>

      <Drawer
        className="right-drawer"
        isOpen={isDrawerOpen}
        showEditForm={showEditForm}
        forceOpen={showEditForm || showRightMask}
        openWidth="25%"
        onOpenChange={setIsDrawerOpen}
        openBtn={openBtnNode}
        closeBtn={closeBtnNode}
      >
        <div className={showRightMask ? 'showRightMask' : 'clearMask'} />
        {isMapReady ? (
          <RightOperate
            searchformValue={rightSearchVal}
            variableList={variableList}
            pageData={pageData}
            changePageCb={rightOperate.changePageCb}
            curSelectPolygonData={curSelectPolygonData}
            panelMode={rightPanelMode}
            formMode={formMode}
            lightMapDraft={session.lightMapDraft}
            createPolygon={coordinatorRef.current!.createPolygon}
            editPolygon={coordinatorRef.current!.editPolygon}
            createLightMap={coordinatorRef.current!.createLightMap}
            continueLightMapParking={coordinatorRef.current!.continueLightMapParking}
            repaintLightMap={coordinatorRef.current!.repaintLightMap}
            cancelLightMap={coordinatorRef.current!.cancelLightMap}
            onLightMapSubTypeChange={handleLightMapSubTypeChange}
            onLightMapTypeSelect={handleLightMapTypeSelect}
            navigationEnums={navigationEnums}
            navigationEnumStatus={navigationEnumStatus}
            retryNavigationEnums={loadNavigationEnums}
            handleClickEdit={rightOperate.handleClickEdit}
            cancelEdit={rightOperate.cancelEdit}
            repaintPolygon={rightOperate.repaintPolygon}
            routePlanMode={planningMode}
            highlightMap={coordinatorRef.current!.highlightMap}
            onDelete={coordinatorRef.current!.deletePolygon}
            updateMapStatus={rightOperate.updateMapStatusWithReopen}
            getOverlayContainer={getOverlayContainer}
            getLaneCoordinates={rightOperate.handleGetLaneCoordinates}
            changeFormSearchVal={rightOperate.changeFormSearchVal}
          />
        ) : (
          // 地图加载窗口期：右侧面板加载占位（AGENTS.md 坑17）
          <div className="right-panel-loading">
            <Spin size="small" />
            <span>加载中...</span>
          </div>
        )}
      </Drawer>

    </div>
  );
};

export default MapEditor;
